import { logToCloudWatch } from '@services/logger.service';

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type MutableRefObject,
} from 'react';
import db from '@services/firebase-service';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  getFeatureByQId,
} from '@services/youtube-platform.service';
import { handleEngagement } from '@services/interactions-service';
import TileManager from '../helper';

export const useRemoveOldTilesState = () => {
  const SAVE_TILES_STATE_TIME_WINDOW = 1000 * 60 * 60 * 24 * 2; // 2 days
  const localKeys = Object.keys(localStorage);
  const talkingTilesKeys = localKeys.filter((key) =>
    key.startsWith('talking-tiles-state')
  );

  talkingTilesKeys.forEach((key) => {
    const localData = localStorage.getItem(key);
    if (!localData) {
      return;
    }
    const data = JSON.parse(localData);
    if (data?.timestamp < Date.now() - SAVE_TILES_STATE_TIME_WINDOW) {
      localStorage.removeItem(key);
    }
  });
};

interface Comment {
  words: string[];
  [key: string]: any;
}

interface RosFeature {
  id: string;
  setting: any;
  color: {
    talkingTilesBgColor?: string;
    talkingTilesTextColor?: string;
    multipleColors?: boolean;
    [key: string]: any;
  };
  comments?: Comment[];
  [key: string]: any;
}

interface RosResult {
  id: string;
  comments: Comment[];
  [key: string]: any;
}

interface InteractionDetails {
  id: string;
  settings: any;
  colors: any;
  [key: string]: any;
}

interface FirestoreUnsub {
  unsub?: () => void;
}

export const useTalkingTilesSetup = (
  currentRosFeature: RosFeature,
  currentRosResult: RosResult[],
  setCurrentFeatureId: (id: string) => void,
  setFeatureId: (id: string) => void,
  setTotalResponders: (noOfResponders: number) => void
) => {
  const [ttId, setttId] = useState<string>('');
  const resultData = useRef<Comment[]>([]);
  const comments = useRef<Comment[]>([]);
  const onTileRemoved = useRef<() => void>(() => { });

  interface InteractionResultItem {
    comments?: any[];
  }

  const setupTalkingTiles = useCallback(
    ({ id, noOfResponders, interactionResult = [] }: { id: string; noOfResponders: number, interactionResult: InteractionResultItem[] }) => {
      comments.current = [];
      setCurrentFeatureId(id);
      setttId(id);
      setFeatureId(id);

      setTotalResponders(noOfResponders);

      if (!(currentRosResult.length > 0)) {
        resultData.current = [];
        return;
      }

      resultData.current = interactionResult?.[0].comments || [];
    },
    [currentRosFeature, currentRosResult, setCurrentFeatureId, setFeatureId]
  );

  return {
    ttId,
    setttId,
    resultData,
    comments,
    setupTalkingTiles,
  };
};

export const useEngagementHandling = (
  streamId: string,
  platformType: string,
  currentRosFeature: RosFeature,
  setupTalkingTiles: (details: { id: string }) => void,
  updateFSMeta: (data: { activeInteraction: any }) => void,
  firestoreUnsub: FirestoreUnsub,
  ttId: string,
  saveTilesLocalStorage: () => void,
  resultData?: MutableRefObject<Comment[]>
) => {
  const [isResultTileLoading, setIsResultTileLoading] =
    useState<boolean>(false);
  const totalCommentsRef = useRef<number>(0);

  const handleTalkingTiles = useCallback(
    async ({ interactionId = null, interactionResult = [] }: { interactionId: null | undefined, interactionResult: [] }) => {
      const response = await handleEngagement({
        streamId,
        platformType,
        currentRosFeature,
        interactionId,
      });
      if (!(response.status && response.entity)) return;

      const interactionDetails: InteractionDetails = {
        id: response.entity.engagementId,
        settings: currentRosFeature.setting,
        colors: currentRosFeature.color,
        noOfResponders: response.entity.noOfResponders || 0,
        interactionResult
      };

      setupTalkingTiles(interactionDetails);

      updateFSMeta({
        activeInteraction: { ...interactionDetails, type: 'talkingTiles' },
      });
    },
    [
      streamId,
      platformType,
      currentRosFeature.id,
      setupTalkingTiles,
      updateFSMeta,
    ]
  );

  const unsubscribeFromFirestore = useCallback(() => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  }, [firestoreUnsub]);

  const stopTalkingTiles = useCallback(async () => {
    saveTilesLocalStorage();
    unsubscribeFromFirestore();
    // const sessionId = localStorage.getItem('sessionId');
    // await stopEngagement(streamId, 'talkingTiles', {
    //   engagementId: ttId,
    //   platformType,
    //   sessionId,
    // });
    setIsResultTileLoading(true);

    const response = await getFeatureByQId(
      currentRosFeature.id,
      streamId,
      'talkingTiles'
    );
    if (!(response.status && response.entity)) return;
    setIsResultTileLoading(false);
  }, [
    saveTilesLocalStorage,
    unsubscribeFromFirestore,
    streamId,
    ttId,
    platformType,
    currentRosFeature.id,
    resultData,
  ]);

  return {
    isResultTileLoading,
    handleTalkingTiles,
    unsubscribeFromFirestore,
    stopTalkingTiles,
    totalCommentsRef,
  };
};

export const useTileManager = (
  containerRef: MutableRefObject<HTMLDivElement | null>,
  currentRosFeature: RosFeature,
  startInteraction: boolean,
  setTiles: (tiles: any[]) => void,
  ttId: string,
  playSound: (() => void) | null
) => {
  const managerRef = useRef<TileManager | null>(null);
  const processedCommentsCount = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const lastTtId = useRef<string>('');
  const processedCommentIds = useRef<Set<string>>(new Set());
  const hasRestoredRef = useRef<boolean>(false);

  const onTileRemovedCallback = useCallback(() => {
    if (typeof playSound === 'function' && isMountedRef.current) {
      playSound();
    }
  }, [playSound]);

  const onTileRemoved = useRef<() => void>(onTileRemovedCallback);

  useEffect(() => {
    onTileRemoved.current = onTileRemovedCallback;
  }, [onTileRemovedCallback]);

  useEffect(() => {
    if (managerRef.current) {
      managerRef.current.setOnTileRemoved(onTileRemoved.current);
    }
  }, [onTileRemovedCallback]);

  const initializeManager = useCallback(() => {
    if (!containerRef.current) return;

    if (managerRef.current && lastTtId.current === ttId) {
      return managerRef.current;
    }

    if (managerRef.current && lastTtId.current !== ttId) {
      managerRef.current.destroy();
    }

    managerRef.current = new TileManager(
      containerRef.current,
      currentRosFeature,
      currentRosFeature?.color?.talkingTilesBgColor,
      currentRosFeature?.color?.multipleColors
        ? '#111111'
        : currentRosFeature.color?.talkingTilesTextColor
    );

    managerRef.current.setCurrentRosFeature(currentRosFeature);
    managerRef.current.setTtId(ttId);
    managerRef.current.setOnTilesUpdated(setTiles);
    managerRef.current.setOnTileRemoved(onTileRemoved.current);

    lastTtId.current = ttId;
    hasRestoredRef.current = false;

    return managerRef.current;
  }, [currentRosFeature, containerRef, setTiles, ttId]);

  useEffect(() => {
    if (!(ttId && startInteraction)) return;

    const manager = initializeManager();
    const container = containerRef.current;
    if (!(manager && container)) return;

    const syncContainerSize = () => {
      manager.setContainer(container);
      manager.setContainerHeight(container.clientHeight || container.getBoundingClientRect().height);
    };

    syncContainerSize();
    const rafId = requestAnimationFrame(syncContainerSize);

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(syncContainerSize);
      observer.observe(container);
    }

    const canUseWindow =
      typeof window !== 'undefined' &&
      typeof window.addEventListener === 'function' &&
      typeof window.removeEventListener === 'function';

    if (canUseWindow) {
      window.addEventListener('resize', syncContainerSize);
    }

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
      if (canUseWindow) {
        window.removeEventListener('resize', syncContainerSize);
      }
    };
  }, [startInteraction, ttId, initializeManager, containerRef]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    processedCommentIds.current.clear();
    hasRestoredRef.current = false;
    processedCommentsCount.current = 0;
  }, [ttId]);

  return {
    managerRef,
    processedCommentsCount,
    initializeManager,
    processedCommentIds,
    hasRestoredRef,
  };
};

export const useFirestoreSubscription = (
  ttId: string,
  isRos: boolean,
  streamId: string,
  setFirestoreUnsub: (unsub: FirestoreUnsub) => void,
  managerRef: MutableRefObject<TileManager | null>,
  processedCommentsCount: MutableRefObject<number>,
  setShowNotification: (show: boolean) => void,
  setTotalResponders: (noOfResponders: number) => void,
  comments?: MutableRefObject<Comment[]>,
  startInteraction?: boolean,
  processedCommentIds?: MutableRefObject<Set<string>>,
  hasRestoredRef?: MutableRefObject<boolean>
) => {
  useEffect(() => {
    if (!ttId) return;

    const logData = {
      streamId,
      interactionType: 'talkingTiles',
      interactionId: ttId,
    };

    const unsubscribe = onSnapshot(
      doc(db, 'streams', streamId, 'talkingTiles', ttId),
      (document) => {
        logToCloudWatch(
          'Successfully subscribed to firestore',
          logData,
          'INFO'
        );
        if (!(document.exists() && managerRef.current)) return;

        let { commentList } = document.data();
        const newComments = commentList;

        if (newComments.length > 0) setShowNotification(false);

        if (!hasRestoredRef?.current) {
          setTimeout(() => {
            if (!managerRef.current) return;
            processFirestoreComments(newComments);
          }, 100);
        } else {
          processFirestoreComments(newComments);
        }

        function processFirestoreComments(commentsToProcess: Comment[]) {
          const commentsToAdd = commentsToProcess.filter((comment: Comment) => {
            const commentId =
              comment.comment_id ||
              comment.id ||
              `${comment.words}-${comment.username}`;

            if (processedCommentIds?.current.has(commentId)) {
              return false;
            }

            processedCommentIds?.current.add(commentId);
            return true;
          });

          if (commentsToAdd.length > 0 && managerRef.current) {
            managerRef.current.dropTile({
              comments: commentsToAdd,
              numCols: managerRef.current.getNumCols(),
            });
            processedCommentsCount.current += commentsToAdd.length;
          }
        }

        if (comments && comments.current) {
          comments.current = newComments;
        }
        setTotalResponders(document.data().noOfResponders || 0);
      },
      (error) =>
        logToCloudWatch(
          'Failed to subscribe to firestore',
          { ...logData, error },
          'ERROR'
        )
    );

    setFirestoreUnsub({
      unsub: unsubscribe,
    });

    return () => {
      unsubscribe();
    };
  }, [ttId, streamId]);
};

export const useSaveTilesState = (
  managerRef: MutableRefObject<TileManager | null>,
  ttId: string,
  startInteraction: boolean
) => {
  const saveTilesLocalStorage = useCallback(() => {
    if (!(managerRef.current && ttId)) return;

    try {
      const tilesState = managerRef.current.getTilesState();

      if (tilesState.length > 0) {
        const stateToSave = {
          comments: tilesState,
          timestamp: Date.now(),
        };
        localStorage.setItem(
          `talking-tiles-state_${ttId}`,
          JSON.stringify(stateToSave)
        );
      }
    } catch (error) {
      console.error('Error saving tiles state:', error);
    }
  }, [ttId, managerRef]);

  return { saveTilesLocalStorage };
};

export const useRestoreTilesState = (
  managerRef: MutableRefObject<TileManager | null>,
  ttId: string,
  startInteraction: boolean,
  currentRosResult: RosResult[]
) => {
  const restoreTilesState = useCallback(() => {
    if (!(ttId && managerRef.current)) return;

    const savedStateString = localStorage.getItem(
      `talking-tiles-state_${ttId}`
    );

    if (!savedStateString) return;

    try {
      const savedState = JSON.parse(savedStateString);
      const currentTime = Date.now();
      const SAVE_TILES_STATE_TIME_WINDOW = 2 * 1000 * 24 * 60 * 60; // 2 days

      if (
        savedState.timestamp &&
        currentTime - savedState.timestamp < SAVE_TILES_STATE_TIME_WINDOW &&
        savedState.comments &&
        savedState.comments.length > 0
      ) {
        managerRef.current.restoreTilesState(savedState.comments);
      }
    } catch (error) {
      console.error('Error restoring tiles state:', error);
    }
  }, [ttId, managerRef]);

  useEffect(() => {
    if (startInteraction && ttId) {
      restoreTilesState();
    }
  }, [startInteraction, ttId]);

  return { restoreTilesState };
};
